import { Button, DatePicker, Input, InputNumber, Spin } from "antd";
import { IMyGoalsMilestoneQuestion, IStateQuickQuestion } from "../mygoals.type";
import { DiamondWhite } from "@/assets/icons";
import { Dispatch, SetStateAction } from "react";
import { userTypeConvert } from "@/utils/helper";
import { Department } from "@/hooks/useGoals";
import { isMobile } from "react-device-detect";
import Spinner from "@/components/Spinner";
import cx from "classnames";

interface IQuestionInputs {
  type: IMyGoalsMilestoneQuestion["dataQuickQuestion"]["type"];
  isLoadingSubmitQuestion: IMyGoalsMilestoneQuestion["isLoadingSubmitQuestion"];
  onSubmit: any;
  stateQuickQuestion: IStateQuickQuestion;
  stateQuickQuestionSet: Dispatch<SetStateAction<IStateQuickQuestion>>;
  parent?: string;
  holdMax: string[];
  holdMaxSet: any;
  max?: IMyGoalsMilestoneQuestion["dataQuickQuestion"]["max"];
  options?: IMyGoalsMilestoneQuestion["dataQuickQuestion"]["options"];
  dataDepartments: Department[] | undefined;
  dataExpertises: any[];
  isHRview?: boolean;
  label?: string;
}

export default function QuestionInputs({
  type,
  parent,
  isLoadingSubmitQuestion,
  onSubmit,
  stateQuickQuestion,
  stateQuickQuestionSet,
  max,
  options,
  holdMax,
  holdMaxSet,
  dataDepartments,
  dataExpertises,
  isHRview,
  label
}: IQuestionInputs) {
  if (isLoadingSubmitQuestion)
    return (
      <div className="w-full flex items-center justify-center">
        <Spinner />
      </div>
    );

  if (type === "boolean")
    return (
      <>
        <Button onClick={() => onSubmit(true)} type="primary">
          <div className="flex items-center justify-between">
            Yes
            {!isHRview && <DiamondWhite />}
          </div>
        </Button>
        <Button onClick={() => onSubmit(false)} type="primary">
          <div className="flex items-center justify-between">
            No
            {!isHRview && <DiamondWhite />}
          </div>
        </Button>
      </>
    );

  if (type === "text")
    return (
      <div className="flex flex-col gap-3">
        <Input
          placeholder="Enter here"
          value={stateQuickQuestion?.text}
          onChange={(e) =>
            stateQuickQuestionSet((prev) => {
              return { ...prev, text: e.target.value };
            })
          }
        />
        <Button
          type="primary"
          disabled={!stateQuickQuestion.text}
          onClick={() => onSubmit(stateQuickQuestion.text, parent)}
        >
          <div className="flex items-center justify-between">
            Submit
            {!isHRview && <DiamondWhite />}
          </div>
        </Button>
      </div>
    );

  if (type === "number")
    return (
      <div className="w-full flex flex-col gap-3">
        <InputNumber
          controls={false}
          style={{ width: "100%" }}
          value={stateQuickQuestion?.number}
          onChange={(e) =>
            stateQuickQuestionSet((prev) => {
              return { ...prev, number: e as number };
            })
          }
        />
        <Button
          type="primary"
          disabled={stateQuickQuestion?.number === null}
          onClick={() => onSubmit(stateQuickQuestion.number)}
        >
          <div className="flex items-center justify-between gap-2">
            Submit
            {!isHRview && <DiamondWhite />}
          </div>
        </Button>
      </div>
    );

  if (type === "options")
    return max ? (
      <div className={cx("flex flex-col gap-4 overflow-y-auto", { "max-h-[300px]": isMobile })}>
        {options?.map((q: any, i: number) => (
          <Button
            style={{ textTransform: "initial" }}
            onClick={() =>
              holdMaxSet((prev: string[]) => {
                const sample = [...prev];
                if (sample.includes(q as string)) {
                  return sample.filter((x) => x !== q);
                }

                if (holdMax.length < max) {
                  sample.push(q);
                }
                return sample;
              })
            }
            key={i}
            type={holdMax.includes(q as never) ? "primary" : undefined}
          >
            <div className="flex items-center justify-between">
              {q}
              {!isHRview && <DiamondWhite />}
            </div>
          </Button>
        ))}

        <Button
          type="primary"
          disabled={holdMax.length === 0}
          onClick={() => {
            onSubmit(holdMax);
            holdMaxSet([]);
          }}
        >
          <div className="flex items-center justify-center">Submit</div>
        </Button>
      </div>
    ) : (
      <div className={cx("flex flex-col gap-4 overflow-y-auto", { "max-h-[300px]": isMobile })}>
        {options?.map((q, i) => {
          let prularOptions = label?.includes(
            "Connections, Mentees, Mentors, or Sponsors",
          );
          return (
            <Button
              style={{
                textTransform: "initial",
                height: "auto",
                whiteSpace: "normal",
                textAlign: "left",
              }}
              onClick={() => onSubmit(q, parent)}
              key={i}
              type="primary"
            >
              <div className="flex items-center justify-between overflow-x-auto">
                {["MENTOR", "SUPER_MENTOR", "MENTEE", "ALLY"].includes(q)
                  ? userTypeConvert(q as never, undefined, prularOptions)
                  : q}
                {!isHRview && <DiamondWhite className="min-w-[16px]" />}
              </div>
            </Button>
          );
        })}
      </div>
    );

  if (type === "date")
    return (
      <div className="flex flex-col gap-3">
        <div className="relative">
          <DatePicker
            style={{ width: "100%" }}
            onChange={(e) =>
              stateQuickQuestionSet((prev) => {
                return { ...prev, date: e };
              })
            }
            className="datepicker-hide-year"
            rootClassName="datepicker-hide-year"
            format="MMMM DD"
          />
        </div>
        <Button
          disabled={!stateQuickQuestion.date ? true : false}
          type="primary"
          onClick={() => onSubmit(stateQuickQuestion.date)}
        >
          <div className="flex items-center justify-between gap-2">
            Submit
            {!isHRview && <DiamondWhite />}
          </div>
        </Button>
      </div>
    );

  if (type === "departments")
    return (
      <div className={cx("flex flex-col gap-4 overflow-y-auto", { "max-h-[300px]": isMobile })}>
        {dataDepartments?.map((item, i) => (
          <Button onClick={() => onSubmit(item?.id)} key={i} type="primary">
            <div className="flex items-center justify-between">
              {item?.name}
              {!isHRview && <DiamondWhite />}
            </div>
          </Button>
        ))}
      </div>
    );

  if (type === "string_array")
    return (
      <div className="flex flex-col gap-4">
        <Input
          placeholder={`Enter here`}
          value={stateQuickQuestion.strings_array_input}
          onChange={({ target: { value } }) =>
            stateQuickQuestionSet((prev: any) => {
              return { ...prev, strings_array_input: value };
            })
          }
        />
        <Button
          type="primary"
          onClick={() => onSubmit(stateQuickQuestion.strings_array_input)}
          disabled={!stateQuickQuestion.strings_array_input}
          block
        >
          <div className="flex items-center justify-between gap-2">
            Submit
            {!isHRview && <DiamondWhite />}
          </div>
        </Button>
      </div>
    );

  if (type === "expertises")
    return (
      <div className={cx("w-full flex flex-col gap-3", { "max-h-[300px] overflow-y-auto": isMobile })}>
        {dataExpertises?.map((item: any, i: any) => (
          <Button type="primary" key={i} onClick={() => onSubmit(item?.id)}>
            <div className="flex items-center justify-between gap-2">
              {item?.name}
              {!isHRview && <DiamondWhite />}
            </div>
          </Button>
        ))}
      </div>
    );
}
